import { db, firebaseEnabled } from '../firebase'
import { collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore'

const LS_PREFIX = 'etimad_admin_'

function lsKey(collectionName) {
  return LS_PREFIX + collectionName
}

function lsRead(collectionName) {
  const raw = localStorage.getItem(lsKey(collectionName))
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function lsWrite(collectionName, items) {
  localStorage.setItem(lsKey(collectionName), JSON.stringify(items))
}

function lsEnsureSeeded(collectionName, seedData) {
  const existing = lsRead(collectionName)
  if (existing) return existing
  const seeded = (seedData || []).map((item, index) => ({ id: `seed-${index}`, ...item }))
  lsWrite(collectionName, seeded)
  return seeded
}

// This app is being run without live Firebase credentials yet. Until those
// are configured, admin data is kept in the browser's localStorage so the
// admin panel is fully usable end-to-end; it will automatically switch to
// Firestore once VUE_APP_FIREBASE_* env vars are set.
export async function listItems(collectionName, seedData) {
  if (firebaseEnabled) {
    const snap = await getDocs(collection(db, collectionName))
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
  }
  return lsEnsureSeeded(collectionName, seedData)
}

// Fetches a single document by id -- a Firestore "get", not a "list" query.
// Used for the public voucher/invoice view links (QR code), where security
// rules allow reading one known document by id without allowing anyone to
// list/enumerate the whole collection.
export async function getItem(collectionName, id) {
  if (firebaseEnabled) {
    const snap = await getDoc(doc(db, collectionName, id))
    return snap.exists() ? { id: snap.id, ...snap.data() } : null
  }
  const items = lsEnsureSeeded(collectionName, [])
  return items.find(item => item.id === id) || null
}

export async function addItem(collectionName, data) {
  if (firebaseEnabled) {
    const ref = await addDoc(collection(db, collectionName), data)
    return { id: ref.id, ...data }
  }
  const items = lsEnsureSeeded(collectionName, [])
  const newItem = { id: `local-${Date.now()}`, ...data }
  items.push(newItem)
  lsWrite(collectionName, items)
  return newItem
}

export async function updateItem(collectionName, id, data) {
  if (firebaseEnabled) {
    await updateDoc(doc(db, collectionName, id), data)
    return { id, ...data }
  }
  const items = lsEnsureSeeded(collectionName, [])
  const idx = items.findIndex(item => item.id === id)
  if (idx !== -1) items[idx] = { id, ...data }
  lsWrite(collectionName, items)
  return { id, ...data }
}

export async function deleteItem(collectionName, id) {
  if (firebaseEnabled) {
    await deleteDoc(doc(db, collectionName, id))
    return
  }
  const items = lsEnsureSeeded(collectionName, []).filter(item => item.id !== id)
  lsWrite(collectionName, items)
}

// Used by the admin "Import Default Data" button. Only adds defaults when
// the collection is currently empty, so it's safe to click more than once.
export async function seedDefaultsIfEmpty(collectionName, seedData) {
  if (firebaseEnabled) {
    const existing = await getDocs(collection(db, collectionName))
    if (!existing.empty) return 0
    for (const item of seedData) {
      await addDoc(collection(db, collectionName), item)
    }
    return seedData.length
  }
  const existing = lsRead(collectionName)
  if (existing && existing.length > 0) return 0
  const seeded = (seedData || []).map((item, index) => ({ id: `seed-${index}`, ...item }))
  lsWrite(collectionName, seeded)
  return seeded.length
}
